import { Injectable } from '@angular/core';
import { ConvertHelper } from '@barthauer/abc/core';
import {
  DataService as CoreDataService,
  ObjectKey,
  PersistenceService,
  PersistMode,
  PersistObjectModel,
  PersistObjectsModel,
  Query,
  QueryColumn,
  QueryColumnSortOrder,
  QueryObjectsModel,
  ReadListDataSourceModel,
  TargetColumnValue,
  TargetObjectData,
} from '@barthauer/abc/core/data';
import { ResourceManager } from '@barthauer/abc/core/resources';
import { exportDataGrid as exportExcelDataGrid } from 'devextreme/excel_exporter';
import { exportDataGrid as exportPdfDataGrid } from 'devextreme/pdf_exporter';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import { Observable, of, Subject } from 'rxjs';
import { exhaustMap, map } from 'rxjs/operators';
import { BibCategory, BibQuestion, BibResponse } from '../models';
import { ReadObjectsInputMain, ReadObjectsInputQueries } from '../classes';
import { ObjectKeys } from '../constants';
import { deleteMultipleInputType, insertMultipleInputType, persistMultipleInputType, updateMultipleInputType, IPersistResult } from '../interfaces';

@Injectable({
  providedIn: 'root',
})
export class DataService {

  constructor(private coreDataService: CoreDataService, private persistenceService: PersistenceService) { }

  public loadListDataSource(objectType: string, columnName: string): Observable<any> {
    const readListDataSourceQuery = new ReadListDataSourceModel();
    const objectKey = new ObjectKey();
    objectKey.ObjectType = objectType;
    readListDataSourceQuery.ObjectKey = objectKey;
    readListDataSourceQuery.OutputMode = 0;
    readListDataSourceQuery.ColumnName = columnName;
    return this.coreDataService.executeReadListDataSource(readListDataSourceQuery);
  }

  public readObjects(objectType: string, columns: QueryColumn[], OPath?: string): Observable<any> {
    const readObjectQuery: QueryObjectsModel = new QueryObjectsModel();
    const mainQuery: Query = new Query();
    mainQuery.ObjectType = objectType;
    mainQuery.Columns = columns;
    mainQuery.OPath = OPath;
    if (OPath) {
      mainQuery.OPath = OPath;
    }
    readObjectQuery.ObjectQueries = [mainQuery];
    return this.coreDataService.executeReadObjectsQuery(readObjectQuery);
  }

  // --------------------------- Pick List

  public getPickListByName(name: string, language: number = 1031): Observable<{ Id: string }[]> {
    const controlItemsColumns = [this.coreDataService.createQueryColumn('Id', 'Id')];
    const opath = `Name='${name}' AND Language='${language}'`;
    return this.readObjects(ObjectKeys.PICKLIST, controlItemsColumns, opath);
  }

  public getPickListtemsByListId(itemId: string): Observable<{ Id: string; Value: string; Description: string }[]> {
    const controlItemsColumns = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('Value', 'Value'),
      this.coreDataService.createQueryColumn('Description', 'Description', QueryColumnSortOrder.Ascending),
    ];
    const opath = `ParentId='${itemId}'`;
    return this.readObjects(ObjectKeys.PICKLISTITEM, controlItemsColumns, opath);
  }

  public getPickListItemsByPickListName(
    pickListName: string,
    language: number = 1031,
    opath?: string
  ): Observable<{ Id: string; Value: string; Description: string }[]> {
    const controlItemsColumns = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('Value', 'Value'),
      this.coreDataService.createQueryColumn('Description', 'Description', QueryColumnSortOrder.Ascending),
    ];
    return this.getPickListByName(pickListName, language).pipe(
      exhaustMap((pickList) => {
        let filter = `ParentId='${pickList[0].Id}'`;
        if (opath) filter = `${filter} AND ${opath}`;
        return this.readObjects(ObjectKeys.PICKLISTITEM, controlItemsColumns, filter);
      })
    );
  }

  // --------------------------- Read Nested

  public returnObjectColumns(columns?: Array<string>, specialColumns?: Array<QueryColumn>): QueryColumn[] {
    if (
      (Array.isArray(columns) && columns.length > 0) ||
      (Array.isArray(specialColumns) && specialColumns.length > 0)
    ) {
      return this.geoDsReadObjectColumns(columns, specialColumns);
    } else {
      return [];
    }
  }

  public geoDsReadObjectColumns(modelProperties: Array<string>, specialColumns?: Array<QueryColumn>): QueryColumn[] {
    const columns: QueryColumn[] = [];

    modelProperties?.forEach((key) => {
      columns.push(this.coreDataService.createQueryColumn(key, key));
    });

    if (specialColumns && specialColumns.length > 0) {
      specialColumns?.forEach((condition) => {
        columns.push(
          this.coreDataService.createQueryColumn(
            condition.OPath,
            condition.Name,
            condition.SortOrder as QueryColumnSortOrder,
            condition.IsFilter,
            condition.IsHidden,
            condition.IsInvariant,
            condition.Format
          )
        );
      });
    }

    return columns;
  }

  public createQueryObjectsModel(data: ReadObjectsInputMain): QueryObjectsModel {
    const query = new Query();

    query.Columns = this.returnObjectColumns(data.columns, data.specialColumns);
    query.ObjectType = data.objectKey;

    if (data.opath) query.OPath = data.opath;

    if (data.limitCount) query.LimitCount = data.limitCount;
    if (data.limitOffset) query.LimitOffset = data.limitOffset;

    if (Array.isArray(data.objectQueries) && data.objectQueries.length) {
      query.ObjectQueries = data.objectQueries.map((object) => this.createQuery(object));
    }

    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectQueries = [query];

    return queryDocument;
  }

  public createQuery(data: ReadObjectsInputQueries): Query {
    const query = new Query();

    query.Columns = this.returnObjectColumns(data.columns, data.specialColumns);

    query.OPath = data.opath;
    query.Name = data.name;

    if (data.limitCount) query.LimitCount = data.limitCount;
    if (data.limitOffset) query.LimitOffset = data.limitOffset;

    if (Array.isArray(data.objectQueries) && data.objectQueries.length) {
      query.ObjectQueries = data.objectQueries.map((object) => this.createQuery(object));
    }

    return query;
  }

  public readObjectData<T>(data: ReadObjectsInputMain): Observable<T[]> {
    const queryObjectsModel = this.createQueryObjectsModel(data);

    return this.coreDataService
      .executeReadObjectsQuery(queryObjectsModel)
      .pipe(map((data) => ConvertHelper.arrayBufferToObjectArray(data) as T[]));
  }

  // --------------------------- Persist Multiple

  private verifyDataArrayEmpty(
    data: insertMultipleInputType[] | updateMultipleInputType[] | deleteMultipleInputType[]
  ): boolean {
    return data.every((element) => element.data.length === 0);
  }

  private getTargetColumnValue(data: Object): TargetColumnValue[] {
    const queryColumns: TargetColumnValue[] = [];
    Object.entries(data).forEach(([property, value]) => {
      if (value !== undefined && this.isPrimitive(value) && property !== 'Id') {
        queryColumns.push({ Name: property, Value: value });
      }
    });
    return queryColumns;
  }

  private isPrimitive(test): boolean {
    return test !== Object(test);
  }

  public persistMultipleData(data: persistMultipleInputType): Observable<any> {
    if (
      (!data.update ||
        (data.update &&
          Array.isArray(data.update) &&
          (data.update.length === 0 || this.verifyDataArrayEmpty(data.update)))) &&
      (!data.insert ||
        (data.insert &&
          Array.isArray(data.insert) &&
          (data.insert.length === 0 || this.verifyDataArrayEmpty(data.insert)))) &&
      (!data.delete ||
        (data.delete &&
          Array.isArray(data.delete) &&
          (data.delete.length === 0 || this.verifyDataArrayEmpty(data.delete))))
    ) {
      return of([]);
    } else {
      const query: PersistObjectsModel = new PersistObjectsModel();
      let persistQuery: TargetObjectData[][] = [[]];
      let index = -1;

      if (
        data.insert &&
        Array.isArray(data.insert) &&
        data.insert.length > 0 &&
        !this.verifyDataArrayEmpty(data.insert)
      ) {
        data.insert?.forEach((toInsert) => {
          let seqNr = Number.isFinite(toInsert.SeqNr) ? toInsert.SeqNr : null;
          if (toInsert.data && Array.isArray(toInsert.data) && toInsert.data.length > 0) {
            index = index + 1;
            persistQuery[index] = [];
            toInsert.data.forEach((objectToInsert) => {
              let targetObject: TargetObjectData = new TargetObjectData();
              targetObject.Mode = PersistMode.Insert;
              targetObject.ObjectKey = new ObjectKey();
              targetObject.ObjectKey.ObjectType = toInsert.modelName;

              if (Number.isFinite(seqNr)) {
                seqNr = seqNr + 1;
                objectToInsert['SeqNr'] = seqNr;
              }
              const columns: TargetColumnValue[] = this.getTargetColumnValue(objectToInsert);
              targetObject.TargetColumns = columns;
              persistQuery[index].push(targetObject);
            });
          }
        });
      }

      if (
        data.update &&
        Array.isArray(data.update) &&
        data.update.length > 0 &&
        !this.verifyDataArrayEmpty(data.update)
      ) {
        data.update?.forEach((toUpdate) => {
          if (toUpdate.data && Array.isArray(toUpdate.data) && toUpdate.data.length > 0) {
            index = index + 1;
            persistQuery[index] = [];

            toUpdate.data.forEach((objectToUpdate) => {
              let targetObject: TargetObjectData = new TargetObjectData();
              targetObject.Mode = PersistMode.Update;
              targetObject.ObjectKey = new ObjectKey();
              targetObject.ObjectKey.Id = objectToUpdate.Id;
              targetObject.ObjectKey.ObjectType = toUpdate.modelName;

              const columns: TargetColumnValue[] = this.getTargetColumnValue(objectToUpdate);
              targetObject.TargetColumns = columns;

              persistQuery[index].push(targetObject);
            });
          }
        });
      }

      if (
        data.delete &&
        Array.isArray(data.delete) &&
        data.delete.length > 0 &&
        !this.verifyDataArrayEmpty(data.delete)
      ) {
        data.delete?.forEach((toDelete) => {
          if (toDelete.data && Array.isArray(toDelete.data) && toDelete.data.length > 0) {
            index = index + 1;
            persistQuery[index] = [];

            toDelete.data.forEach((objectIdToDelete) => {
              let targetObject: TargetObjectData = new TargetObjectData();
              targetObject.Mode = PersistMode.Delete;
              targetObject.ObjectKey = new ObjectKey();
              targetObject.ObjectKey.ObjectType = toDelete.modelName;

              targetObject.ObjectKey.Id = objectIdToDelete;

              persistQuery[index].push(targetObject);
            });
          }
        });
      }
      query.Objects = persistQuery;
      return this.persistenceService.executePersistObjectsQuery(query);
    }
  }

  // --------------------------- Persist single

  public updateObject<T>(objectKey: ObjectKeys, id: string, data: T): Observable<IPersistResult> {
    if (!objectKey || !data || !id) {
      return of(null);
    }

    const persistQuery: PersistObjectModel = new PersistObjectModel();

    const targetObject: TargetObjectData = new TargetObjectData();
    targetObject.Mode = PersistMode.Update;

    targetObject.ObjectKey = new ObjectKey();
    targetObject.ObjectKey.Id = id;
    targetObject.ObjectKey.ObjectType = objectKey;

    targetObject.TargetColumns = this.getTargetColumnValue(data);

    persistQuery.Object = targetObject;
    return this.persistenceService
      .executePersistObjectQuery(persistQuery)
      .pipe(map((data) => ConvertHelper.arrayBufferToObject(data)));
  }

  public insertObject<T>(objectKey: ObjectKeys, data: T): Observable<IPersistResult> {
    if (!objectKey || !data) {
      return of(null);
    }

    const persistQuery: PersistObjectModel = new PersistObjectModel();

    const targetObject: TargetObjectData = new TargetObjectData();
    targetObject.Mode = PersistMode.Insert;

    targetObject.ObjectKey = new ObjectKey();
    targetObject.ObjectKey.ObjectType = objectKey;

    targetObject.TargetColumns = this.getTargetColumnValue(data);

    persistQuery.Object = targetObject;
    return this.persistenceService
      .executePersistObjectQuery(persistQuery)
      .pipe(map((data) => ConvertHelper.arrayBufferToObject(data)));
  }

  public deleteObject(objectKey: ObjectKeys, id: string): Observable<IPersistResult> {
    const persistQuery: PersistObjectModel = new PersistObjectModel();
    const targetObject: TargetObjectData = new TargetObjectData();
    targetObject.Mode = PersistMode.Delete;
    targetObject.ObjectKey = new ObjectKey();
    targetObject.ObjectKey.Id = id;
    targetObject.ObjectKey.ObjectType = objectKey;
    persistQuery.Object = targetObject;
    return this.persistenceService
      .executePersistObjectQuery(persistQuery)
      .pipe(map((data) => ConvertHelper.arrayBufferToObject(data)));
  }
}
